import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminListPage } from '../components/AdminListPage'
import { ClientDriveCell } from '../components/ClientDriveCell'
import { useAdminClientEngagements } from '../hooks/useAdminClientEngagements'
import { useAdminClientDrives } from '../hooks/useAdminClientDrive'
import type { ClientEngagementDocument, EngagementStatus } from '@/types'

type Engagement = ClientEngagementDocument & { id: string }

interface ClientRow {
  clientUserId: string
  clientName: string
  clientEmail: string
  engagementCount: number
  totalRevenue: number
  lastEngagementAt: Date | null
}

type SortKey = 'name' | 'count' | 'revenue' | 'avg' | 'last'
type SortDir = 'asc' | 'desc'

// Estimates from cancelled engagements aren't real revenue.
const REVENUE_EXCLUDED_STATUSES: ReadonlySet<EngagementStatus> = new Set(['cancelled'])

function engagementDate(eng: Engagement): Date | null {
  if (eng.scheduledDate) {
    const d = new Date(eng.scheduledDate + 'T00:00:00')
    if (!Number.isNaN(d.getTime())) return d
  }
  if (eng.createdAt instanceof Date) return eng.createdAt
  return null
}

function formatDate(d: Date | null): string {
  if (!d) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

function aggregateClients(engagements: Engagement[]): ClientRow[] {
  const byId = new Map<string, ClientRow>()
  for (const eng of engagements) {
    if (!eng.clientUserId) continue
    const existing = byId.get(eng.clientUserId)
    const engDate = engagementDate(eng)
    const isRevenue = !REVENUE_EXCLUDED_STATUSES.has(eng.status) && typeof eng.estimateAmount === 'number'
    const revenueDelta = isRevenue ? (eng.estimateAmount ?? 0) : 0
    if (existing) {
      existing.engagementCount += 1
      existing.totalRevenue += revenueDelta
      if (engDate && (!existing.lastEngagementAt || engDate > existing.lastEngagementAt)) {
        existing.lastEngagementAt = engDate
      }
      // Prefer the most recent record's denormalized name/email
      if (engDate && existing.lastEngagementAt && engDate >= existing.lastEngagementAt) {
        existing.clientName = eng.clientName || existing.clientName
        existing.clientEmail = eng.clientEmail || existing.clientEmail
      }
    } else {
      byId.set(eng.clientUserId, {
        clientUserId: eng.clientUserId,
        clientName: eng.clientName || '(unknown)',
        clientEmail: eng.clientEmail || '',
        engagementCount: 1,
        totalRevenue: revenueDelta,
        lastEngagementAt: engDate,
      })
    }
  }
  return Array.from(byId.values())
}

function SortHeader({
  label,
  active,
  dir,
  align,
  onClick,
}: {
  label: string
  active: boolean
  dir: SortDir
  align?: 'left' | 'right'
  onClick: () => void
}) {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors ' +
        (align === 'right' ? 'justify-end w-full' : '')
      }
    >
      <span>{label}</span>
      <Icon className="h-3 w-3" />
    </button>
  )
}

export function AdminClientsPage() {
  const { engagements, loading, error } = useAdminClientEngagements()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('last')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const rows = useMemo(() => aggregateClients(engagements), [engagements])

  const clientUids = useMemo(() => rows.map((r) => r.clientUserId), [rows])
  const {
    drives,
    loading: drivesLoading,
    provision,
    link: linkDrive,
    unlink: unlinkDrive,
  } = useAdminClientDrives(clientUids)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (r) =>
        r.clientName.toLowerCase().includes(q) ||
        r.clientEmail.toLowerCase().includes(q),
    )
  }, [rows, search])

  const sorted = useMemo(() => {
    const dirMul = sortDir === 'asc' ? 1 : -1
    const copy = [...filtered]
    copy.sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.clientName.localeCompare(b.clientName) * dirMul
        case 'count':
          return (a.engagementCount - b.engagementCount) * dirMul
        case 'revenue':
          return (a.totalRevenue - b.totalRevenue) * dirMul
        case 'avg': {
          const aAvg = a.engagementCount > 0 ? a.totalRevenue / a.engagementCount : 0
          const bAvg = b.engagementCount > 0 ? b.totalRevenue / b.engagementCount : 0
          return (aAvg - bAvg) * dirMul
        }
        case 'last': {
          const aT = a.lastEngagementAt?.getTime() ?? 0
          const bT = b.lastEngagementAt?.getTime() ?? 0
          return (aT - bT) * dirMul
        }
      }
    })
    return copy
  }, [filtered, sortKey, sortDir])

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  const totalClients = rows.length
  const totalRevenue = rows.reduce((s, r) => s + r.totalRevenue, 0)
  const totalEngagements = rows.reduce((s, r) => s + r.engagementCount, 0)

  return (
    <AdminListPage
      title="Clients"
      filters={
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
          <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
            <span>
              <strong className="text-foreground">{totalClients}</strong>{' '}
              {totalClients === 1 ? 'client' : 'clients'}
            </span>
            <span>
              <strong className="text-foreground">{totalEngagements}</strong> engagements
            </span>
            <span>
              <strong className="text-foreground">{formatCurrency(totalRevenue)}</strong> revenue
            </span>
          </div>
        </div>
      }
      loading={loading}
      error={error}
      skeletonRows={6}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortHeader
                label="Client"
                active={sortKey === 'name'}
                dir={sortDir}
                onClick={() => toggleSort('name')}
              />
            </TableHead>
            <TableHead className="text-right">
              <SortHeader
                label="Engagements"
                active={sortKey === 'count'}
                dir={sortDir}
                align="right"
                onClick={() => toggleSort('count')}
              />
            </TableHead>
            <TableHead className="text-right">
              <SortHeader
                label="Total Revenue"
                active={sortKey === 'revenue'}
                dir={sortDir}
                align="right"
                onClick={() => toggleSort('revenue')}
              />
            </TableHead>
            <TableHead className="text-right">
              <SortHeader
                label="Avg / Engagement"
                active={sortKey === 'avg'}
                dir={sortDir}
                align="right"
                onClick={() => toggleSort('avg')}
              />
            </TableHead>
            <TableHead className="text-right">
              <SortHeader
                label="Last Engagement"
                active={sortKey === 'last'}
                dir={sortDir}
                align="right"
                onClick={() => toggleSort('last')}
              />
            </TableHead>
            <TableHead>Drive</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                {rows.length === 0 ? 'No clients yet.' : 'No clients match your search.'}
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((r) => (
              <TableRow
                key={r.clientUserId}
                className="cursor-pointer"
                onClick={() =>
                  navigate(`/admin/client-services/engagements?clientUserId=${encodeURIComponent(r.clientUserId)}`)
                }
              >
                <TableCell>
                  <div className="font-medium text-sm">{r.clientName}</div>
                  <div className="text-xs text-muted-foreground">{r.clientEmail || '—'}</div>
                </TableCell>
                <TableCell className="text-right text-sm font-mono">
                  {r.engagementCount}
                </TableCell>
                <TableCell className="text-right text-sm font-mono">
                  {formatCurrency(r.totalRevenue)}
                </TableCell>
                <TableCell className="text-right text-sm font-mono">
                  {r.engagementCount > 0
                    ? formatCurrency(r.totalRevenue / r.engagementCount)
                    : '—'}
                </TableCell>
                <TableCell className="text-right text-sm text-muted-foreground whitespace-nowrap">
                  {formatDate(r.lastEngagementAt)}
                </TableCell>
                <TableCell>
                  <ClientDriveCell
                    clientUserId={r.clientUserId}
                    clientName={r.clientName}
                    drive={drives.get(r.clientUserId)}
                    loading={drivesLoading && !drives.has(r.clientUserId)}
                    onProvision={provision}
                    onLink={linkDrive}
                    onUnlink={unlinkDrive}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </AdminListPage>
  )
}
